import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  Search,
  Package,
  Store,
  Calendar,
  Hash,
  TrendingDown,
  RotateCcw,
  Eye,
  XCircle,
  Clock,
  Pencil,
  ArrowRight,
  Loader2,
  FileText,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import SageProductSelector from '@/components/retail/SageProductSelector';
import { useStoreMapping } from '@/hooks/useStoreMapping';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Stock Issues Management - Stocks por Resolver
 * 
 * This component shows ISL movements that couldn't be posted due to insufficient stock.
 * Embedded as a Tab within RetailAuditLog.
 * 
 * Users can:
 * - View pending stock issues with details
 * - Revalidate stock (check if now available)
 * - View issue history
 * 
 * CANONICAL API: /api/stock-issues (from routes/stock_issues.py)
 */

// Static helper to fetch open count for Badge display
export async function fetchStockIssuesCount() {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/stock-issues/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data?.total_open || 0;
  } catch (err) {
    console.error('Error fetching stock issues count:', err);
    return 0;
  }
}

// Status badge colors
const STATUS_COLORS = {
  OPEN: 'bg-amber-100 text-amber-800 border-amber-200',
  READY_TO_RETRY: 'bg-blue-100 text-blue-800 border-blue-200',
  RESOLVED: 'bg-green-100 text-green-800 border-green-200',
  CANCELLED: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STATUS_LABELS = {
  OPEN: 'Por Resolver',
  READY_TO_RETRY: 'Pronto p/ Tentar',
  RESOLVED: 'Resolvido',
  CANCELLED: 'Cancelado',
};

export default function StockIssues({ initialSearch = '' }) {
  const { getStoreName } = useStoreMapping();
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [totalOpen, setTotalOpen] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [months, setMonths] = useState([]);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [totals, setTotals] = useState({ total: 0, open: 0, ready: 0, units: 0 });
  const [stores, setStores] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [revalidating, setRevalidating] = useState({});
  
  // SKU Correction dialog state
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionIssue, setCorrectionIssue] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({
    corrected_sage_sku: '',
    create_permanent_mapping: false,
    reason: '',
  });
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [correctionError, setCorrectionError] = useState(null);
  // Document detail modal state
  const [docDetailRef, setDocDetailRef] = useState(null);
  const [docDetailData, setDocDetailData] = useState(null);
  const [loadingDocDetail, setLoadingDocDetail] = useState(false);
  // Product search for autocomplete
  const [productSearch, setProductSearch] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const PAGE_SIZE = 200;

  const loadIssues = useCallback(async (append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (storeFilter) params.set('store_code', storeFilter);
      if (monthFilter) params.set('month', monthFilter);
      if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
      params.set('limit', String(PAGE_SIZE));
      params.set('skip', String(append ? issues.length : 0));

      const res = await axios.get(`${API}/api/stock-issues/?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const page = res.data.issues || [];
      setIssues(prev => (append ? [...prev, ...page] : page));
      setTotalOpen(res.data.total_open || 0);
      setTotals({
        total: res.data.total || 0,
        open: res.data.open || 0,
        ready: res.data.ready_to_retry || 0,
        units: res.data.pending_units || 0,
      });
      setStores(res.data.stores || []);
      setMonths(res.data.months || []);
    } catch (err) {
      console.error('Error loading stock issues:', err);
      toast.error('Erro ao carregar stocks por resolver');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, storeFilter, monthFilter, debouncedSearch]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (initialSearch) setSearchQuery(initialSearch);
  }, [initialSearch]);

  const filteredIssues = issues;

  // Handle revalidate stock
  const handleRevalidate = async (issueId) => {
    setRevalidating(prev => ({ ...prev, [issueId]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/stock-issues/${issueId}/revalidate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.is_sufficient) {
        toast.success(`Stock suficiente! ${res.data.sku} tem ${res.data.available_stock} unidades`);
      } else {
        toast.info(`Stock ainda insuficiente: ${res.data.available_stock} < ${res.data.pending_quantity}`);
      }
      
      loadIssues();
    } catch (err) {
      console.error('Error revalidating:', err);
      toast.error('Erro ao revalidar stock');
    } finally {
      setRevalidating(prev => ({ ...prev, [issueId]: false }));
    }
  };

  // ============================================================================
  // SKU CORRECTION FUNCTIONS
  // ============================================================================
  
  // Search products for autocomplete
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/products`, {
        params: { search: query, limit: 15 },
        headers: { Authorization: `Bearer ${token}` },
      });
      // API returns 'items' not 'products' - filter out SERVICE products
      const products = res.data.items || res.data.products || [];
      const physical = products.filter(p => !p.is_service);
      setProductSearchResults(physical);
    } catch (err) {
      console.error('Error searching products:', err);
      setProductSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  }, []);
  
  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) {
        searchProducts(productSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);
  
  // Handle product selection from autocomplete
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setCorrectionForm(prev => ({
      ...prev,
      corrected_sage_sku: product.sku,
    }));
    setProductSearch('');
    setProductSearchResults([]);
  };
  
  // Open correction dialog
  const handleOpenCorrectionDialog = (issue) => {
    setCorrectionIssue(issue);
    setCorrectionForm({
      corrected_sage_sku: '',
      create_permanent_mapping: false, // Default: one-time correction
      reason: '',
    });
    setSelectedProduct(null);
    setProductSearch('');
    setCorrectionError(null);
    setShowCorrectionDialog(true);
  };
  
  // Save SKU correction
  const handleSaveCorrection = async () => {
    if (!correctionForm.corrected_sage_sku || !correctionIssue) {
      setCorrectionError('Selecione um artigo SAGE');
      return;
    }
    
    setSavingCorrection(true);
    setCorrectionError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      const res = await axios.post(
        `${API}/api/stock-issues/${correctionIssue.issue_id}/correct-sku`,
        {
          corrected_sage_sku: correctionForm.corrected_sage_sku,
          create_permanent_mapping: correctionForm.create_permanent_mapping,
          reason: correctionForm.reason || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { stock_check, permanent_mapping_created, new_status } = res.data;
      
      if (stock_check?.is_sufficient) {
        toast.success(
          <div>
            <strong>Correção aplicada com sucesso</strong>
            <div className="text-xs mt-1">
              Stock disponível: {stock_check.available} unidades
              {permanent_mapping_created && <span className="block mt-1 text-emerald-600">Mapeamento permanente criado</span>}
            </div>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.info(
          <div>
            <strong>SKU corrigido, stock insuficiente</strong>
            <div className="text-xs mt-1">
              Disponível: {stock_check?.available || 0} | Necessário: {stock_check?.required || 0}
              {permanent_mapping_created && <span className="block mt-1 text-emerald-600">Mapeamento permanente criado</span>}
            </div>
          </div>,
          { duration: 5000 }
        );
      }
      
      setShowCorrectionDialog(false);
      await loadIssues();
    } catch (err) {
      console.error('Correction error:', err);
      setCorrectionError(err.response?.data?.detail || 'Erro ao corrigir SKU');
    } finally {
      setSavingCorrection(false);
    }
  };

  // ============================================================================
  // DOCUMENT DETAIL FUNCTIONS
  // ============================================================================
  
  // Load document allocations when docDetailRef changes
  useEffect(() => {
    if (!docDetailRef) {
      setDocDetailData(null);
      return;
    }
    
    const loadDocDetail = async () => {
      setLoadingDocDetail(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/api/stock-issues/document/${encodeURIComponent(docDetailRef)}/allocations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDocDetailData(res.data);
      } catch (err) {
        console.error('Error loading document detail:', err);
        toast.error('Erro ao carregar detalhes do documento');
        setDocDetailRef(null);
      } finally {
        setLoadingDocDetail(false);
      }
    };
    
    loadDocDetail();
  }, [docDetailRef]);

  // Stats cards (server-side totals for current filters)
  const stats = {
    total: totals.total,
    open: totals.open,
    ready: totals.ready,
    totalPending: totals.units,
  };

  return (
    <div className="space-y-6" data-testid="stock-issues-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-xl font-semibold text-slate-900">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Por Resolver</p>
              <p className="text-xl font-semibold text-slate-900">{stats.open}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <RotateCcw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Prontos</p>
              <p className="text-xl font-semibold text-slate-900">{stats.ready}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Package className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Unidades Pendentes</p>
              <p className="text-xl font-semibold text-slate-900">{stats.totalPending}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Pesquisar por SKU, descrição, batch..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>
          
          {/* Store Filter */}
          <select
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="store-filter"
          >
            <option value="">Todas as Lojas</option>
            {stores.map(code => <option key={code} value={code}>{getStoreName(code)}</option>)}
          </select>

          {/* Month Filter (business date) */}
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="stock-issues-month-filter"
          >
            <option value="">Todos os meses</option>
            {months.map(m => {
              const label = new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
              return <option key={m} value={m}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>;
            })}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="status-filter"
          >
            <option value="">Todos os Estados</option>
            <option value="OPEN">Por Resolver</option>
            <option value="READY_TO_RETRY">Prontos p/ Tentar</option>
            <option value="RESOLVED">Resolvidos</option>
            <option value="CANCELLED">Cancelados</option>
          </select>
          
          {/* Refresh */}
          <button
            onClick={() => loadIssues()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Issues List */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
          <p className="mt-2 text-sm text-slate-500">A carregar...</p>
        </div>
      ) : filteredIssues.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-slate-900" data-testid="issues-empty-title">
            {debouncedSearch || storeFilter || statusFilter || monthFilter ? 'Sem resultados para os filtros' : 'Sem stocks por resolver'}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {debouncedSearch || storeFilter || statusFilter || monthFilter
              ? 'Experimente alterar a pesquisa, a loja, o mês ou o estado.'
              : 'Todos os movimentos de stock foram processados com sucesso.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="issues-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Loja</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Data</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">SKU</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Descrição</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-700">Necessário</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-700">Stock</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-700">Falta</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-700">Estado</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Documento SAGE</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredIssues.map((issue) => {
                  const originalSku = issue.original_sku || issue.sku;
                  const correctedSku = issue.corrected_sage_sku;
                  const hasSageDoc = issue.sage_document_reference;
                  const allocations = issue.sage_allocations || [];
                  
                  return (
                  <tr key={issue.issue_id} className="hover:bg-slate-50" data-testid={`issue-row-${issue.issue_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{getStoreName(issue.store_code)}</span>
                      </div>
                      <div className="text-xs text-slate-400 ml-6" data-testid={`issue-warehouse-${issue.issue_id}`}>
                        Armazém {issue.warehouse || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {issue.business_date}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <code className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono">
                          {originalSku}
                        </code>
                        {correctedSku && correctedSku !== originalSku && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 text-xs">→</span>
                            <code className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-mono">
                              {correctedSku}
                            </code>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[150px] truncate">
                      {issue.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {issue.required_quantity}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {issue.last_stock_check_result ?? issue.available_quantity_at_attempt ?? '-'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-red-600">
                      {issue.pending_quantity}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[issue.status] || 'bg-slate-100'}`}>
                        {STATUS_LABELS[issue.status] || issue.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {hasSageDoc ? (
                        <button
                          onClick={() => setDocDetailRef(issue.sage_document_reference)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-blue-50 text-blue-700 hover:bg-blue-100 rounded transition-colors"
                          title="Ver detalhes do documento"
                          data-testid={`doc-ref-${issue.issue_id}`}
                        >
                          <FileText className="w-3 h-3" />
                          {issue.sage_document_reference}
                        </button>
                      ) : allocations.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {allocations.slice(0, 2).map((alloc, idx) => (
                            <button
                              key={idx}
                              onClick={() => setDocDetailRef(alloc.document_reference)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 hover:bg-blue-100 rounded transition-colors"
                            >
                              <FileText className="w-3 h-3" />
                              {alloc.document_reference}
                            </button>
                          ))}
                          {allocations.length > 2 && (
                            <span className="text-xs text-slate-500">+{allocations.length - 2} mais</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {/* Revalidate Stock */}
                        {(issue.status === 'OPEN' || issue.status === 'READY_TO_RETRY') && (
                          <button
                            onClick={() => handleRevalidate(issue.issue_id)}
                            disabled={revalidating[issue.issue_id]}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors disabled:opacity-50"
                            title="Revalidar Stock"
                            data-testid={`revalidate-btn-${issue.issue_id}`}
                          >
                            <RotateCcw className={`w-3 h-3 ${revalidating[issue.issue_id] ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                        
                        {/* Correct SKU - New button */}
                        {(issue.status === 'OPEN' || issue.status === 'READY_TO_RETRY') && !issue.sage_document_reference && (
                          <button
                            onClick={() => handleOpenCorrectionDialog(issue)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded transition-colors"
                            title="Corrigir Artigo"
                            data-testid={`correct-btn-${issue.issue_id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                        
                        {/* View Details (placeholder for future modal) */}
                        <button
                          className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Ver Detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
            <span data-testid="issues-count-label">A mostrar {issues.length} de {totals.total}</span>
            {issues.length < totals.total && (
              <Button size="sm" variant="outline" onClick={() => loadIssues(true)} disabled={loadingMore} data-testid="load-more-btn">
                {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Carregar mais
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Info Note */}
      {filteredIssues.length === 0 && !loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex gap-3">
            <Package className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-900">Como funcionam os Stocks por Resolver?</h4>
              <p className="mt-1 text-sm text-blue-700">
                Quando um ficheiro WinTouch contém vendas de artigos sem stock suficiente no SAGE, 
                essas vendas ficam pendentes aqui. Quando o stock for corrigido no SAGE (via compra, 
                transferência ou ajuste), pode revalidar e criar os movimentos ISL em falta.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* SKU Correction Dialog */}
      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Corrigir Artigo
            </DialogTitle>
            <DialogDescription>
              O SKU registado no WinTouch está errado? Indique o artigo SAGE correto.
            </DialogDescription>
          </DialogHeader>
          
          {correctionError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {correctionError}
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {/* Issue info */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between">
                <Label className="text-xs text-slate-500">Loja</Label>
                <span className="font-medium">{getStoreName(correctionIssue?.store_code)}</span>
              </div>
              <div className="flex justify-between">
                <Label className="text-xs text-slate-500">Armazém SAGE</Label>
                <span className="font-medium">{correctionIssue?.warehouse || 'Não configurado'}</span>
              </div>
              <div className="flex justify-between">
                <Label className="text-xs text-slate-500">Data</Label>
                <span>{correctionIssue?.business_date}</span>
              </div>
              <div className="flex justify-between">
                <Label className="text-xs text-slate-500">SKU WinTouch</Label>
                <span className="font-mono font-medium">{correctionIssue?.sku}</span>
              </div>
              <div className="flex justify-between">
                <Label className="text-xs text-slate-500">Descrição</Label>
                <span className="text-sm max-w-xs truncate">{correctionIssue?.description || '—'}</span>
              </div>
              <div className="flex justify-between">
                <Label className="text-xs text-slate-500">Quantidade Pendente</Label>
                <span className="font-medium text-amber-700">{correctionIssue?.pending_quantity}</span>
              </div>
              <div className="flex justify-between">
                <Label className="text-xs text-slate-500">Stock Detectado</Label>
                <span>{correctionIssue?.last_stock_check || '—'}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-center text-slate-400">
              <ArrowRight className="w-6 h-6" />
            </div>
            
            {/* SAGE SKU with shared SageProductSelector - shows stock info */}
            <SageProductSelector
              label="Artigo Correto no SAGE"
              placeholder="Pesquisar artigo no catálogo..."
              value={selectedProduct}
              onChange={(product) => {
                setSelectedProduct(product);
                setCorrectionForm(prev => ({
                  ...prev,
                  corrected_sage_sku: product?.sku || '',
                }));
              }}
              disabled={savingCorrection}
              error={correctionError && !selectedProduct ? "Selecione um artigo SAGE válido" : null}
              showStockInfo={true}
              warehouse={correctionIssue?.warehouse || null}
              data-testid="sage-product-selector-correction"
            />
            
            {/* Correction type: one-time vs permanent */}
            <div className="space-y-3">
              <Label>Tipo de Correção</Label>
              <RadioGroup
                value={correctionForm.create_permanent_mapping ? 'permanent' : 'onetime'}
                onValueChange={(val) => setCorrectionForm(prev => ({
                  ...prev,
                  create_permanent_mapping: val === 'permanent'
                }))}
                className="space-y-2"
              >
                <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                  <RadioGroupItem value="onetime" id="onetime" className="mt-0.5" />
                  <div>
                    <Label htmlFor="onetime" className="font-medium cursor-pointer">
                      Corrigir apenas esta ocorrência
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Aplica apenas a este stock issue. Não afeta futuras vendas do mesmo SKU.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                  <RadioGroupItem value="permanent" id="permanent" className="mt-0.5" />
                  <div>
                    <Label htmlFor="permanent" className="font-medium cursor-pointer">
                      Corrigir e criar mapeamento permanente
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Todas as futuras vendas do SKU WinTouch serão automaticamente convertidas para o SKU SAGE.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
            
            {/* Reason */}
            <div>
              <Label htmlFor="correction-reason">Motivo (opcional)</Label>
              <Input
                id="correction-reason"
                placeholder="Ex: Loja registou código errado..."
                value={correctionForm.reason}
                onChange={(e) => setCorrectionForm(prev => ({ ...prev, reason: e.target.value }))}
                data-testid="correction-reason-input"
              />
            </div>
          </div>
          
          {selectedProduct && correctionIssue && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="correction-confirm-summary">
              Vai enviar ao SAGE <strong className="font-mono">{selectedProduct.sku}</strong>
              {' '}({selectedProduct.name}) em vez de{' '}
              <strong className="font-mono">{correctionIssue.original_sku || correctionIssue.sku}</strong>.
              Confirme que é o artigo certo.
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveCorrection} 
              disabled={savingCorrection || !selectedProduct}
              data-testid="save-correction-btn"
            >
              {savingCorrection ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A corrigir...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aplicar Correção
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Detail Modal */}
      <Dialog open={!!docDetailRef} onOpenChange={(open) => !open && setDocDetailRef(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Detalhes do Documento SAGE
            </DialogTitle>
            <DialogDescription>
              Rastreabilidade: Stock Issues → Documento SAGE
            </DialogDescription>
          </DialogHeader>
          
          {loadingDocDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : docDetailData ? (
            <div className="space-y-4">
              {/* Document Reference */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="font-mono font-semibold text-lg text-blue-900">
                    {docDetailRef}
                  </span>
                </div>
                {docDetailData.summary && (
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <span className="text-slate-500">Issues incluídos:</span>
                      <span className="ml-2 font-medium">{docDetailData.summary.total_issues}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Quantidade total:</span>
                      <span className="ml-2 font-medium">{docDetailData.summary.total_quantity}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">SKUs SAGE:</span>
                      <span className="ml-2 font-mono text-xs">{docDetailData.summary.effective_skus?.join(', ')}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Lojas:</span>
                      <span className="ml-2">{docDetailData.summary.stores?.join(', ')}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Allocations Table */}
              {docDetailData.allocations?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">Issue ID</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">SKU Original</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">SKU SAGE</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-700">Qtd</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">Correção</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {docDetailData.allocations.map((alloc, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs">{alloc.issue_id}</td>
                          <td className="px-3 py-2">
                            <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">
                              {alloc.original_sku}
                            </code>
                          </td>
                          <td className="px-3 py-2">
                            <code className="px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-xs">
                              {alloc.effective_sage_sku}
                            </code>
                          </td>
                          <td className="px-3 py-2 text-right font-medium">{alloc.quantity}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">
                            {alloc.correction_type === 'ONE_TIME' && 'One-time'}
                            {alloc.correction_type === 'PERMANENT_MAPPING' && 'Permanente'}
                            {!alloc.correction_type && '—'}
                            {alloc.corrected_by && (
                              <span className="block text-slate-400">por {alloc.corrected_by}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {!docDetailData.found && (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>Nenhum Stock Issue encontrado para este documento</p>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocDetailRef(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
